import React, { createContext, useContext, useState, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import Icon from '../components/AppIcon'

const ToastContext = createContext(null)

const TOAST_CONFIG = {
  success: { icon: 'CheckCircle', accent: 'border-l-success', iconColor: 'text-success' },
  error:   { icon: 'XCircle',     accent: 'border-l-destructive', iconColor: 'text-destructive' },
  warning: { icon: 'AlertTriangle', accent: 'border-l-warning', iconColor: 'text-warning' },
  info:    { icon: 'Info',         accent: 'border-l-primary',    iconColor: 'text-primary' },
}

let toastId = 0

function Toast({ toast, onDismiss }) {
  const config = TOAST_CONFIG[toast.type] || TOAST_CONFIG.info

  return (
    <div
      className={`
        pointer-events-auto w-80 max-w-[calc(100vw-2rem)]
        bg-card/90
        border border-white/10 border-l-4 ${config.accent}
        rounded-xl shadow-glass-lg
        transition-all duration-300
        ${toast.exiting ? 'animate-slide-out-right' : 'animate-slide-in-right'}
      `}
      role="alert"
    >
      <div className="flex items-start gap-3 p-4">
        <Icon name={config.icon} size={18} className={`flex-shrink-0 mt-0.5 ${config.iconColor}`} />
        <div className="flex-1 min-w-0">
          {toast.title && (
            <p className="text-sm font-semibold text-foreground">{toast.title}</p>
          )}
          <p className={`text-sm text-foreground/80 ${toast.title ? 'mt-0.5' : ''}`}>
            {toast.message}
          </p>
        </div>
        <button
          onClick={() => onDismiss(toast.id)}
          className="flex-shrink-0 text-foreground/40 hover:text-foreground/70 transition-colors"
        >
          <Icon name="X" size={14} />
        </button>
      </div>
    </div>
  )
}

function ToastContainer({ toasts, onDismiss }) {
  if (toasts.length === 0) return null

  return createPortal(
    <div
      className="fixed top-4 right-4 z-[9999] flex flex-col gap-3 pointer-events-none"
      aria-live="polite"
    >
      {toasts.map(toast => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>,
    document.body
  )
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const timersRef = useRef({})

  const dismiss = useCallback((id) => {
    // Start exit animation
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t))
    // Remove after animation
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    }, 300)
    clearTimeout(timersRef.current[id])
    delete timersRef.current[id]
  }, [])

  const addToast = useCallback((type, message, options = {}) => {
    const id = ++toastId
    const duration = options.duration ?? 4000

    setToasts(prev => [...prev, { id, type, message, title: options.title, exiting: false }])

    if (duration > 0) {
      timersRef.current[id] = setTimeout(() => dismiss(id), duration)
    }

    return id
  }, [dismiss])

  const toast = useCallback((message, options) => addToast('info', message, options), [addToast])
  toast.success = useCallback((message, options) => addToast('success', message, options), [addToast])
  toast.error = useCallback((message, options) => addToast('error', message, options), [addToast])
  toast.warning = useCallback((message, options) => addToast('warning', message, options), [addToast])
  toast.info = useCallback((message, options) => addToast('info', message, options), [addToast])

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}
